import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import uniqBy from "lodash/uniqBy";

import { MarketingSummaryAnalyticsService } from "@analytics/index";

import {
  createUserFile,
  deleteChatHistory,
  loadChatMessages,
  persistChatMessages,
  rateChatMessage,
} from "@api/chat.api";

import C from "@constants/constants";

import {
  CreateUserFileRequestType,
  CreateUserFileResponseType,
  LoadChatMessagesResponseType,
  RateChatRequestType,
  RateChatResponseType,
} from "@interfaces/chat.type";

import { IMessage } from "@pages/Technology/components/TechnologyMarketingSummary";

import { PopupActions } from "@store/popupStore";

const markdownContent = `
Ready to create your marketing summary? Here's how to get started:

#### Key Features
 - Summary or description of your technology
 - Key benefits
 - Applications

#### Attach Template (Optional)
Have a preferred format? Upload an example PDF to guide your summary's structure.

#### Guide Creation
Refine your summary through AI conversation - request additions, suggest rewrites, or adjust sections.
`;

export const useLoadMessages = (technologyId: string) => {
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery<LoadChatMessagesResponseType>({
    queryFn: async () => {
      const response = await loadChatMessages(technologyId);
      if (response.marketingSummary && response.marketingSummary.length > 0) {
        return response;
      }
      return {
        files: response.files,
        marketingSummary: [
          {
            content: markdownContent,
            id: Date.now().toString(),
            role: "instruction",
          },
        ],
        ratings: [],
        summaryId: "",
        technologyId: technologyId,
      };
    },
    queryKey: ["chat", technologyId],
    staleTime: C.STALE_TIME,
  });

  /*
    setMessages is a function that can be used to update the messages state.
    It can be called with a function that takes the current messages array as an argument and returns a new array.
    Or it can be called with an array of messages to replace the current messages array.
   */
  const setMessages = (messagesOrUpdater: ((previous: IMessage[]) => IMessage[]) | IMessage[]) => {
    const currentMessages = queryClient.getQueryData<LoadChatMessagesResponseType>(["chat", technologyId]);

    if (!currentMessages || !currentMessages.marketingSummary) {
      return;
    }

    // If messagesOrUpdater is a function, call it with the current messages array
    // and use the result as the new messages array
    // Otherwise, use messagesOrUpdater as the new messages array
    const newMessages =
      typeof messagesOrUpdater === "function" ? messagesOrUpdater(currentMessages.marketingSummary) : messagesOrUpdater;

    void queryClient.setQueryData(["chat", technologyId], {
      ...currentMessages,
      marketingSummary: newMessages,
    });
  };

  return {
    files: data?.files ?? [],
    isLoadingMessages: isPending,
    messages: data?.marketingSummary ?? [],
    setMessages,
    summaryId: data?.summaryId ?? "",
  };
};

interface IPersistMessagesRequestType {
  messages: IMessage[];
  technologyId: string;
}

export const usePersistMessages = () => {
  const queryClient = useQueryClient();

  return useMutation<string, Error, IPersistMessagesRequestType>({
    mutationFn: async ({ messages, technologyId }) => {
      const data = await persistChatMessages(technologyId, messages);
      return data.id;
    },
    onSuccess: (summaryId, { technologyId }) => {
      const currentMessages = queryClient.getQueryData<LoadChatMessagesResponseType>(["chat", technologyId]);
      if (!currentMessages || currentMessages.summaryId === summaryId) return;
      queryClient.setQueryData(["chat", technologyId], (previousData) => {
        if (!previousData) return previousData;
        return {
          ...previousData,
          summaryId,
        };
      });
    },
  });
};

export const useDeleteChatHistory = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { technologyId: string }>({
    mutationFn: async ({ technologyId }) => {
      await deleteChatHistory(technologyId);
    },
    onSuccess: (_, { technologyId }) => {
      void queryClient.invalidateQueries({ queryKey: ["chat", technologyId] });
      MarketingSummaryAnalyticsService.trackMarketingSummaryDeleted();
    },
  });
};

export const useRateChatMessage = () => {
  const queryClient = useQueryClient();
  return useMutation<
    RateChatResponseType,
    Error,
    { technologyId: string } & RateChatRequestType,
    LoadChatMessagesResponseType
  >({
    mutationFn: async (request) => {
      return await rateChatMessage(request);
    },
    onError: (_, { technologyId }, previousData) => {
      if (previousData) {
        queryClient.setQueryData(["chat", technologyId], previousData);
      }
      PopupActions.openNotification({
        content: "There was an issue rating your chat message. Please try again.",
        title: "Error",
      });
    },
    onMutate: (request) => {
      const { messageId, summaryId, technologyId } = request;

      const oldMessages = queryClient.getQueryData<LoadChatMessagesResponseType>(["chat", summaryId]);
      queryClient.setQueryData<LoadChatMessagesResponseType>(["chat", technologyId], (previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          marketingSummary: (previous?.marketingSummary || []).map((message) =>
            message.id == messageId ? { ...message, isRated: true } : message,
          ),
        };
      });

      return oldMessages;
    },
    onSuccess: (data) => {
      if (data.rating === "negative") {
        MarketingSummaryAnalyticsService.trackSummaryNegativelyRated(
          data.promptType,
          data?.issues?.join(", ") ?? "",
          data.userFeedback ?? "",
        );
      } else {
        MarketingSummaryAnalyticsService.trackSummaryPositivelyRated();
      }

      PopupActions.openAlert({
        duration: 4000,
        message: "Thank you for your feedback!",
        severity: "success",
      });
    },
  });
};

export const useCreateUserFile = () => {
  const queryClient = useQueryClient();
  return useMutation<CreateUserFileResponseType, Error, { technologyId: string } & CreateUserFileRequestType>({
    mutationFn: async ({ fileName, structure, userId }) => {
      return await createUserFile({ fileName, structure, userId });
    },
    onError: (_, { technologyId }, previousData) => {
      if (previousData) {
        queryClient.setQueryData(["chat", technologyId], previousData);
      }
      PopupActions.openNotification({
        content: "There was an issue creating your file. Please try again.",
        title: "Error",
      });
    },
    onSuccess: (data, { technologyId }) => {
      queryClient.setQueryData<LoadChatMessagesResponseType>(["chat", technologyId], (previousData) => {
        if (!previousData) return;
        const newFiles = uniqBy([...(previousData?.files || []), data], "fileName");
        return {
          ...previousData,
          files: newFiles,
        };
      });
      MarketingSummaryAnalyticsService.trackFileUploaded();
    },
  });
};
